import { useEffect, useRef, type ComponentType } from 'react'

import { useWindow, useWindowShell } from '@krp/client-runtime'
import { DUPLICATES_SECTION, DUPLICATES_WINDOWS } from '@krp/duplicates-ui'
import { PLATFORM_SECTIONS, PLATFORM_WINDOWS } from '@krp/platform-ui'
import { StatusMessage } from '@krp/ui-kit'

import { useSectionTabs } from '../../app/sectionTabs'
import { SECTION_HEADERS } from '../../composition/sectionHeaders'
import { SectionResourcePicker } from './SectionResourcePicker'

/**
 * Вход раздела без собственного содержимого (UI-SECTIONS): «Поставки»,
 * «Продажа»… Раздел открывает первую вкладку — окно первого ресурса, который
 * его умеет, — ЗАМЕНЯЯ себя (адрес раздела в истории не остаётся, «назад» не
 * возвращает в него же). Ресурсов нет — понятное пустое состояние со ссылкой
 * на «Источники», а не пустой экран.
 */
function SectionLanding({ sectionId }: { sectionId: string }) {
  const window = useWindow()
  const shell = useWindowShell()
  const section = shell.section(sectionId)
  const sectionTabs = useSectionTabs(section)
  const { query, tabs } = sectionTabs
  const pick = section?.pickResource === true
  // Вкладка с замком — вид без окна раздела: открывать в ней нечего.
  const first = tabs.find((tab) => !tab.locked)
  const locked = tabs.filter((tab) => tab.locked).map((tab) => tab.badge ?? tab.kind)
  // Один переход на вход: перечитанный список ресурсов не должен заменить окно второй раз.
  const opened = useRef(false)

  useEffect(() => {
    // Раздел с плитками сам вкладку не открывает: источник выбирает человек.
    if (pick || opened.current || first?.resourceId == null) return
    opened.current = true
    window.replaceWith(first.kind, { rid: first.resourceId })
  }, [first, pick, window])

  if (section === undefined) return null
  const Header = SECTION_HEADERS[sectionId]
  if (pick) return <>{Header !== undefined && <Header />}<SectionResourcePicker section={section} sectionTabs={sectionTabs} /></>
  if (query.status === 'ready' && first === undefined) {
    return (
      <StatusMessage
        kind="empty"
        title={`В разделе «${section.label}» пока нет источников`}
        text={`Ни один доступный вам ресурс не умеет этот раздел, или право к нему вам не выдано.${
          locked.length > 0 ? ` В ${locked.join(', ')} такого раздела нет.` : ''
        } Источники подключает и права выдаёт администратор.`}
      />
    )
  }
  if (query.status === 'ready' || query.status === 'loading' || query.status === 'idle') {
    return <StatusMessage kind="loading" title={`Открываем раздел «${section.label}»…`} />
  }
  return (
    <StatusMessage
      kind={query.status === 'forbidden' ? 'forbidden' : query.status === 'unauthorized' ? 'unauthorized' : 'unavailable'}
      title="Не удалось узнать, какие источники вам доступны"
      {...(query.error !== null ? { text: query.error.message } : {})}
    />
  )
}

function landing(sectionId: string): ComponentType {
  const Binding = () => <SectionLanding sectionId={sectionId} />
  Binding.displayName = `SectionLandingBinding(${sectionId})`
  return Binding
}

/** Вход раздела → его раздел. «Номенклатура» сюда не входит: её вход — общий каталог. */
const LANDINGS: readonly (readonly [string, string])[] = [
  [PLATFORM_WINDOWS.receipts, PLATFORM_SECTIONS.receipts],
  [PLATFORM_WINDOWS.salesStatistics, PLATFORM_SECTIONS.salesStatistics],
  [PLATFORM_WINDOWS.shifts, PLATFORM_SECTIONS.shifts],
  [PLATFORM_WINDOWS.supplies, PLATFORM_SECTIONS.supplies],
  [PLATFORM_WINDOWS.prices, PLATFORM_SECTIONS.prices],
  [PLATFORM_WINDOWS.inventory, PLATFORM_SECTIONS.inventory],
  [PLATFORM_WINDOWS.priceTags, PLATFORM_SECTIONS.priceTags],
  [PLATFORM_WINDOWS.import, PLATFORM_SECTIONS.import],
  [PLATFORM_WINDOWS.structure, PLATFORM_SECTIONS.structure],
  [DUPLICATES_WINDOWS.home, DUPLICATES_SECTION],
]

/** Binding входа каждого раздела: один компонент, разное имя раздела. */
export const SECTION_LANDING_BINDINGS: Readonly<Record<string, ComponentType>> = Object.fromEntries(
  LANDINGS.map(([kind, section]) => [kind, landing(section)]),
)
